import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Loader2, Star, Send, CheckCircle, Flashlight, FlashlightOff, FileSignature, X } from 'lucide-react';
import { toast } from 'sonner';
import Daily from '@daily-co/daily-js';
import SignatureFlow from '../components/SignatureFlow';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_techlogger-2/artifacts/sb51dzqw_IMG_0974.png';
const MASCOT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files/kardif/assets/mascot-duck-v2.png`;

const fileHref = (u) => (!u ? '' : (/^https?:\/\//.test(u) ? u : `${process.env.REACT_APP_BACKEND_URL}${u.startsWith('/') ? '' : '/'}${u}`));

export default function VisioClient() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callState, setCallState] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [flashSupported, setFlashSupported] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [signing, setSigning] = useState(false);
  const [lastSignedId, setLastSignedId] = useState(null);
  const [dismissedId, setDismissedId] = useState(null);
  
  const callObjectRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const videoContainerRef = useRef(null);
  const localStreamRef = useRef(null);

  // Fetch session info
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await fetch(`${API}/visio/join/${token}`);
        if (!res.ok) throw new Error('Session non trouvée');
        const data = await res.json();
        setSession(data);
      } catch (err) {
        setError(err.message);
        toast.error('Lien invalide ou session expirée');
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [token]);

  // Start local camera preview FIRST, then join Daily for audio/video transmission
  const joinCall = useCallback(async () => {
    if (!session || callObjectRef.current) return;
    
    if (!session.room_url && session.status !== 'in_progress') {
      toast.error('Le technicien n\'a pas encore démarré l\'appel.');
      return;
    }
    
    setCallState('joining');
    
    try {
      // STEP 1: Get local camera stream in HIGH QUALITY
      let localStream;
      
      // High quality video constraints
      const hdVideoConstraints = {
        facingMode: 'environment',
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
        frameRate: { ideal: 30, min: 15 }
      };
      
      try {
        // Try rear camera first (environment) in HD
        localStream = await navigator.mediaDevices.getUserMedia({ 
          video: hdVideoConstraints, 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
      } catch (e) {
        console.log('HD failed, trying standard quality');
        try {
          // Fallback to standard HD
          localStream = await navigator.mediaDevices.getUserMedia({ 
            video: { 
              facingMode: 'environment',
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }, 
            audio: true 
          });
        } catch (e2) {
          // Final fallback to any camera
          localStream = await navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: true 
          });
        }
      }
      
      localStreamRef.current = localStream;
      
      // Get video track for quality check and flash support
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        // Log actual video quality
        const settings = videoTrack.getSettings();
        console.log(`Video quality: ${settings.width}x${settings.height} @ ${settings.frameRate}fps`);
        
        // Check if flash/torch is supported
        const capabilities = videoTrack.getCapabilities?.();
        if (capabilities?.torch) {
          setFlashSupported(true);
        }
      }
      
      // Display local video immediately
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStream;
        // Try to play, set state based on result
        try {
          await localVideoRef.current.play();
          setVideoPlaying(true);
        } catch (e) {
          console.log('Autoplay blocked, user needs to tap');
          setVideoPlaying(false);
        }
      }
      
      // STEP 2: Create Daily call object and join (for transmission to technician)
      const callObject = Daily.createCallObject({
        videoSource: localStream.getVideoTracks()[0],
        audioSource: localStream.getAudioTracks()[0],
      });
      
      callObjectRef.current = callObject;
      
      callObject.on('joined-meeting', () => {
        setCallState('joined');
        toast.success('Connecté ! Le technicien peut vous voir.');
      });

      // Jouer l'audio/vidéo du TECHNICIEN (sinon le client ne l'entend pas)
      const attachRemote = (participant) => {
        if (!participant || participant.local) return;
        const audioTrack = participant.audioTrack || participant.tracks?.audio?.track || participant.tracks?.audio?.persistentTrack;
        if (audioTrack && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = new MediaStream([audioTrack]);
          remoteAudioRef.current.play().catch(() => {});
        }
        const videoTrack = participant.videoTrack || participant.tracks?.video?.track || participant.tracks?.video?.persistentTrack;
        if (videoTrack && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = new MediaStream([videoTrack]);
          remoteVideoRef.current.play().catch(() => {});
        }
      };
      callObject.on('track-started', (event) => {
        if (event.participant.local) return;
        if (event.track.kind === 'audio' && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = new MediaStream([event.track]);
          remoteAudioRef.current.play().catch(() => {});
        } else if (event.track.kind === 'video' && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = new MediaStream([event.track]);
          remoteVideoRef.current.play().catch(() => {});
        }
      });
      callObject.on('participant-updated', (event) => attachRemote(event.participant));
      
      callObject.on('left-meeting', () => {
        setCallState('ended');
        setShowRating(true);
      });
      
      callObject.on('error', (e) => {
        console.error('Daily error:', e);
        toast.error('Erreur de connexion');
        setCallState('idle');
      });
      
      const roomUrl = session.room_url || `https://rdfvisio.daily.co/${session.room_name}`;
      
      await callObject.join({
        url: roomUrl,
        userName: session.client_name || 'Client',
      });
      
    } catch (err) {
      console.error('Join error:', err);
      toast.error('Impossible de rejoindre l\'appel');
      setCallState('idle');
      if (callObjectRef.current) {
        callObjectRef.current.destroy();
        callObjectRef.current = null;
      }
    }
  }, [session]);

  // Leave call
  const leaveCall = async () => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    // Leave Daily call
    if (callObjectRef.current) {
      await callObjectRef.current.leave();
      callObjectRef.current.destroy();
      callObjectRef.current = null;
    }
    setCallState('ended');
    setShowRating(true);
  };

  // Start video playback (for iOS tap-to-play requirement)
  const startVideoPlayback = async () => {
    if (localVideoRef.current && localStreamRef.current) {
      try {
        localVideoRef.current.srcObject = localStreamRef.current;
        await localVideoRef.current.play();
        setVideoPlaying(true);
      } catch (e) {
        console.error('Could not play video:', e);
      }
    }
  };

  // Toggle mute
  const toggleMute = () => {
    // Mute local stream audio
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted; // Toggle: if muted, enable; if not muted, disable
      });
    }
    if (callObjectRef.current) {
      callObjectRef.current.setLocalAudio(isMuted);
    }
    setIsMuted(!isMuted);
  };

  // Toggle video
  const toggleVideo = () => {
    // Toggle local stream video
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = isVideoOff; // Toggle
      });
    }
    if (callObjectRef.current) {
      callObjectRef.current.setLocalVideo(isVideoOff);
    }
    setIsVideoOff(!isVideoOff);
  };

  // Toggle flash/torch
  const toggleFlash = async () => {
    if (!localStreamRef.current) return;
    
    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;
    
    try {
      const newFlashState = !flashOn;
      await videoTrack.applyConstraints({
        advanced: [{ torch: newFlashState }]
      });
      setFlashOn(newFlashState);
      toast.success(newFlashState ? 'Flash activé' : 'Flash désactivé');
    } catch (err) {
      console.error('Flash error:', err);
      toast.error('Impossible d\'activer le flash');
    }
  };

  // Draw annotations on canvas - EXACTLY like technician sees them
  const drawAnnotations = useCallback((ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    
    if (annotations.length === 0) return;
    
    annotations.forEach(ann => {
      const x = ann.x * width;
      const y = ann.y * height;
      
      ctx.strokeStyle = ann.color || '#EF4444';
      ctx.fillStyle = ann.color || '#EF4444';
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Shadow for visibility
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 3;
      
      switch (ann.type) {
        case 'arrow':
          if (ann.startX !== undefined && ann.endX !== undefined) {
            const startX = ann.startX * width;
            const startY = ann.startY * height;
            const endX = ann.endX * width;
            const endY = ann.endY * height;
            
            // Draw line
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();
            
            // Draw arrowhead
            const angle = Math.atan2(endY - startY, endX - startX);
            const headLength = 30;
            ctx.beginPath();
            ctx.moveTo(endX, endY);
            ctx.lineTo(
              endX - headLength * Math.cos(angle - Math.PI / 6),
              endY - headLength * Math.sin(angle - Math.PI / 6)
            );
            ctx.lineTo(
              endX - headLength * Math.cos(angle + Math.PI / 6),
              endY - headLength * Math.sin(angle + Math.PI / 6)
            );
            ctx.closePath();
            ctx.fill();
          }
          break;
          
        case 'circle':
          ctx.beginPath();
          const radius = (ann.radius || 0.05) * Math.min(width, height);
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.stroke();
          break;
          
        case 'freehand':
          if (ann.path && ann.path.length > 1) {
            ctx.beginPath();
            ctx.moveTo(ann.path[0].x * width, ann.path[0].y * height);
            ann.path.forEach(p => ctx.lineTo(p.x * width, p.y * height));
            ctx.stroke();
          }
          break;
          
        case 'text':
          if (ann.text) {
            ctx.font = 'bold 28px Arial';
            ctx.fillText(ann.text, x, y);
          }
          break;
          
        default:
          break;
      }
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    });
  }, [annotations]);

  // Resize canvas and redraw - sync with video dimensions
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = videoContainerRef.current;
    if (!canvas || !container || callState !== 'joined') return;
    
    const resizeAndDraw = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      
      const ctx = canvas.getContext('2d');
      drawAnnotations(ctx, canvas.width, canvas.height);
    };
    
    resizeAndDraw();
    
    // Redraw frequently to stay in sync
    const interval = setInterval(resizeAndDraw, 100);
    window.addEventListener('resize', resizeAndDraw);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeAndDraw);
    };
  }, [annotations, callState, drawAnnotations]);

  // Poll annotations from backend - FAST polling for real-time feel
  useEffect(() => {
    if (callState !== 'joined' || !token) return;
    
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API}/visio/annotations/${token}`);
        if (res.ok) {
          const data = await res.json();
          if (data.annotations) {
            setAnnotations(prev => {
              // Only update if actually changed
              if (JSON.stringify(prev) !== JSON.stringify(data.annotations)) {
                return data.annotations;
              }
              return prev;
            });
          }
        }
      } catch (e) {
        // Silent fail - keep polling
      }
    }, 200); // Poll every 200ms for real-time feel
    
    return () => clearInterval(poll);
  }, [callState, token]);

  // Poll for a document pushed by the technician to be signed
  useEffect(() => {
    if (!token || callState === 'ended') return;
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API}/visio/active-document/${token}`);
        if (res.ok) {
          const data = await res.json();
          setActiveDoc((prev) => {
            const next = data.active_document || null;
            if (next && dismissedId && next.id === dismissedId) return null;
            if (JSON.stringify(prev) !== JSON.stringify(next)) return next;
            return prev;
          });
        }
      } catch (e) {}
    }, 1500);
    return () => clearInterval(poll);
  }, [token, callState, dismissedId]);

  const signActiveDoc = async (signature, placements) => {
    if (!activeDoc || !signature) return;
    setSigning(true);
    try {
      const res = await fetch(`${API}/visio/sign-document/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature, signatures: placements?.signatures || [], texts: placements?.texts || [] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Échec de la signature');
      }
      const data = await res.json();
      setLastSignedId(data.document?.id || Date.now());
      setActiveDoc(null);
      toast.success('Document signé ! Merci.');
    } catch (e) {
      toast.error(e.message || 'Impossible de signer le document');
    } finally {
      setSigning(false);
    }
  };
  useEffect(() => {
    if (!session || callState !== 'idle' || session.status === 'in_progress') return;
    
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API}/visio/join/${token}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'in_progress' && data.room_url) {
            setSession(data);
            toast.success('Le technicien a démarré l\'appel !');
            clearInterval(poll);
          }
        }
      } catch (e) {}
    }, 5000);
    
    return () => clearInterval(poll);
  }, [session, token, callState]);

  // Submit rating
  const submitRating = async () => {
    try {
      await fetch(`${API}/visio/rate/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, feedback }),
      });
      setRatingSubmitted(true);
      toast.success('Merci pour votre retour !');
    } catch (e) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop local camera stream
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      // Destroy Daily call object
      if (callObjectRef.current) {
        callObjectRef.current.destroy();
      }
    };
  }, []);

  // Loading screen
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#8B7355] animate-spin" />
      </div>
    );
  }

  // Error screen
  if (error || !session) {
    return (
      <div className="fixed inset-0 bg-[#1A1A1A] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Session invalide</h2>
            <p className="text-gray-600">Ce lien n'est plus valide ou la session a expiré.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Rating screen (after call ends)
  if (showRating && !ratingSubmitted) {
    return (
      <div className="fixed inset-0 bg-[#1A1A1A] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <h2 className="text-xl font-bold text-center mb-6">Comment s'est passé l'appel ?</h2>
            <div className="flex justify-center gap-2 mb-6">
              {[1,2,3,4,5].map((star) => (
                <button key={star} onClick={() => setRating(star)} className="p-1">
                  <Star className={`h-10 w-10 ${rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Commentaire (optionnel)"
              className="w-full border rounded-lg p-3 mb-4 h-24 resize-none"
            />
            <Button onClick={submitRating} className="w-full bg-[#8B7355] hover:bg-[#6B5344]">
              <Send className="h-4 w-4 mr-2" />
              Envoyer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Thank you screen
  if (ratingSubmitted) {
    return (
      <div className="fixed inset-0 bg-[#1A1A1A] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Merci !</h2>
            <p className="text-gray-600">Votre retour a bien été enregistré.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main call interface
  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Overlay : document à signer poussé par le technicien */}
      {activeDoc && (
        <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="client-sign-overlay">
          <div className="bg-[#8B7355] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              <span className="font-semibold text-sm">Document à signer</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/80 truncate max-w-[40vw]">{activeDoc.name}</span>
              <button onClick={() => { setDismissedId(activeDoc.id); setActiveDoc(null); }} className="p-1 rounded hover:bg-white/15" data-testid="client-sign-close"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <SignatureFlow
              pdfUrl={fileHref(activeDoc.url)}
              docName={activeDoc.name}
              busy={signing}
              onComplete={signActiveDoc}
              onCancel={() => { setDismissedId(activeDoc.id); setActiveDoc(null); }}
            />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-3 z-50 flex items-center justify-between">
        <img src={LOGO_URL} alt="KARDIF" className="h-6" />
        <span className="text-white/80 text-xs">Visio-Assistance</span>
      </div>

      {/* Badge Technicien KARDIF (Kardy) — le client ne voit pas le visage du technicien */}
      {callState === 'joined' && (
        <div className="absolute top-14 left-3 z-50 flex items-center gap-2 bg-black/55 backdrop-blur-sm rounded-full pl-1 pr-3 py-1" data-testid="visio-kardy-badge">
          <img src={MASCOT_URL} alt="Kardy" className="h-9 w-9 rounded-full bg-white/90 object-contain p-0.5" />
          <div className="leading-tight">
            <p className="text-white text-xs font-semibold">Technicien KARDIF</p>
            <p className="text-white/60 text-[10px]">en ligne avec vous</p>
          </div>
        </div>
      )}
      
      {/* Main video area */}
      <div className="flex-1 relative" ref={videoContainerRef}>
        
        {/* YOUR CAMERA - FULL SCREEN (this is what the technician sees and draws on) */}
        {callState === 'joined' && (
          <>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              onClick={startVideoPlayback}
              className="absolute inset-0 w-full h-full object-contain bg-black"
              style={{ zIndex: 1 }}
            />
            
            {/* TAP TO START - if video not playing */}
            {!videoPlaying && (
              <div 
                className="absolute inset-0 flex items-center justify-center z-20 bg-black/80"
                onClick={startVideoPlayback}
              >
                <div className="text-center p-6">
                  <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Video className="h-10 w-10 text-white" />
                  </div>
                  <p className="text-white text-xl font-bold mb-2">Appuyez pour activer votre caméra</p>
                  <p className="text-white/60 text-sm">Votre caméra va s'afficher en plein écran</p>
                </div>
              </div>
            )}
            
            {/* ANNOTATIONS CANVAS - ON TOP OF YOUR CAMERA */}
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ zIndex: 30 }}
            />

            {/* Audio du TECHNICIEN (pour l'entendre) */}
            <audio ref={remoteAudioRef} autoPlay playsInline />
            {/* Vidéo du technicien en miniature */}
            <div className="absolute top-3 right-3 w-28 h-20 rounded-lg overflow-hidden border-2 border-white/40 shadow-xl bg-black" style={{ zIndex: 35 }}>
              <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
              <span className="absolute bottom-0.5 left-1 text-white text-[10px] bg-black/50 px-1 rounded">Technicien</span>
            </div>
          </>
        )}
        
        {/* Annotation indicator */}
        {callState === 'joined' && annotations.length > 0 && (
          <div className="absolute top-14 left-1/2 transform -translate-x-1/2 z-40">
            <div className="bg-red-600 rounded-full px-5 py-2 flex items-center gap-2 shadow-xl animate-pulse">
              <div className="w-3 h-3 bg-white rounded-full" />
              <span className="text-white text-sm font-bold">Le technicien vous montre quelque chose</span>
            </div>
          </div>
        )}
        
        {/* Waiting screen */}
        {callState === 'idle' && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A] z-10">
            <Card className="max-w-md w-full mx-4">
              <CardContent className="p-8 text-center">
                <Video className="h-16 w-16 text-[#8B7355] mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">
                  {session.status === 'in_progress' ? 'Le technicien vous attend !' : 'En attente du technicien'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {session.status === 'in_progress' 
                    ? 'Appuyez sur Rejoindre pour démarrer l\'appel vidéo.'
                    : 'Le technicien va bientôt démarrer la session...'}
                </p>
                
                <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-600"><strong>Technicien :</strong> {session.technician_name}</p>
                  <p className="text-sm text-gray-600"><strong>Date :</strong> {new Date(session.scheduled_date).toLocaleDateString('fr-FR')}</p>
                  {session.status !== 'in_progress' && (
                    <div className="mt-3 flex items-center gap-2 text-amber-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">En attente...</span>
                    </div>
                  )}
                </div>
                
                <Button
                  data-testid="visio-join-call"
                  onClick={joinCall}
                  disabled={session.status !== 'in_progress'}
                  className={`w-full h-14 text-lg ${session.status === 'in_progress' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'}`}
                >
                  <Phone className="h-6 w-6 mr-2" />
                  {session.status === 'in_progress' ? 'Rejoindre l\'appel' : 'En attente...'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Joining screen */}
        {callState === 'joining' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-50">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
              <p className="text-white">Connexion en cours...</p>
              <p className="text-white/60 text-sm">Autorisez l'accès à la caméra et au micro</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Call controls */}
      {callState === 'joined' && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-4 z-50">
          {/* Flash button - only show if supported */}
          {flashSupported && (
            <Button
              data-testid="visio-toggle-flash"
              onClick={toggleFlash}
              className={`rounded-full w-12 h-12 ${flashOn ? 'bg-yellow-500' : 'bg-white/20 backdrop-blur-sm'}`}
            >
              {flashOn ? <Flashlight className="h-5 w-5 text-white" /> : <FlashlightOff className="h-5 w-5 text-white" />}
            </Button>
          )}
          
          <Button
            data-testid="visio-toggle-mute"
            onClick={toggleMute}
            className={`rounded-full w-14 h-14 ${isMuted ? 'bg-red-500' : 'bg-white/20 backdrop-blur-sm'}`}
          >
            {isMuted ? <MicOff className="h-6 w-6 text-white" /> : <Mic className="h-6 w-6 text-white" />}
          </Button>
          
          <Button
            data-testid="visio-toggle-video"
            onClick={toggleVideo}
            className={`rounded-full w-14 h-14 ${isVideoOff ? 'bg-red-500' : 'bg-white/20 backdrop-blur-sm'}`}
          >
            {isVideoOff ? <VideoOff className="h-6 w-6 text-white" /> : <Video className="h-6 w-6 text-white" />}
          </Button>
          
          <Button
            data-testid="visio-leave-call"
            onClick={leaveCall}
            className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700"
          >
            <PhoneOff className="h-7 w-7 text-white" />
          </Button>
        </div>
      )}
    </div>
  );
}
